import { useState } from "react";
import { toast } from "sonner";
import { Field, Card, SaveButton, GhostButton, inputCls, areaCls } from "../ui";
import { adminFetch } from "../adminApi";

export default function AdminServiceArea({ content, reload }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(content.service_area || {})));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const update = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const updatePlace = (idx, value) =>
    setDraft((d) => ({ ...d, places: (d.places || []).map((p, i) => (i === idx ? value : p)) }));
  const addPlace = () => setDraft((d) => ({ ...d, places: [...(d.places || []), ""] }));
  const removePlace = (idx) => setDraft((d) => ({ ...d, places: (d.places || []).filter((_, i) => i !== idx) }));

  const numOrNull = (v) => (v === "" || v === null ? null : Number(v));

  const save = async () => {
    setSaving(true);
    try {
      await adminFetch("/admin/content/service_area", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...draft,
          free_radius_km: numOrNull(draft.free_radius_km),
          max_radius_km: numOrNull(draft.max_radius_km),
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await reload();
      toast.success("Einsatzgebiet gespeichert – live auf der Website.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-zinc-400 leading-relaxed">
        Alles hier ist optional: Leere Felder und Werte werden auf der Website einfach nicht
        angezeigt. Erfinde nichts – trage nur ein, was für dich stimmt (z. B. freier Anfahrtsradius
        oder Pauschale).
      </p>

      <Card title="Einsatzgebiet & Anfahrt">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Ausgangsort">
            <input data-testid="admin-area-origin" className={inputCls} value={draft.origin || ""} onChange={(e) => update("origin", e.target.value)} maxLength={120} placeholder="z. B. 27356 Rotenburg" />
          </Field>
          <Field label="Kostenfreier Radius (km, leer = keine Angabe)">
            <input
              type="number"
              min="0"
              data-testid="admin-area-free-radius"
              className={inputCls}
              value={draft.free_radius_km ?? ""}
              onChange={(e) => update("free_radius_km", e.target.value === "" ? null : Number(e.target.value))}
            />
          </Field>
          <Field label="Anfahrtskosten (Freitext, leer = keine Angabe)">
            <input data-testid="admin-area-fee" className={inputCls} value={draft.travel_fee || ""} onChange={(e) => update("travel_fee", e.target.value)} maxLength={120} placeholder="z. B. 0,50 €/km ab 20 km" />
          </Field>
          <Field label="Maximaler Einsatzradius (km, leer = keine Angabe)">
            <input
              type="number"
              min="0"
              data-testid="admin-area-max-radius"
              className={inputCls}
              value={draft.max_radius_km ?? ""}
              onChange={(e) => update("max_radius_km", e.target.value === "" ? null : Number(e.target.value))}
            />
          </Field>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">Einzelne Orte / Gebiete</p>
            <GhostButton onClick={addPlace} testid="admin-area-place-add">+ Ort</GhostButton>
          </div>
          {(draft.places || []).map((p, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_auto] gap-3 items-center mb-2">
              <input className={inputCls} value={p} onChange={(e) => updatePlace(idx, e.target.value)} maxLength={120} placeholder="z. B. Visselhövede" />
              <GhostButton danger onClick={() => removePlace(idx)} testid={`admin-area-place-delete-${idx}`}>
                ×
              </GhostButton>
            </div>
          ))}
        </div>

        <Field label="Individueller Hinweis">
          <textarea
            data-testid="admin-area-note"
            className={areaCls}
            value={draft.note || ""}
            onChange={(e) => update("note", e.target.value)}
            maxLength={1000}
            placeholder="z. B. Termine außerhalb des Radius auf Anfrage"
          />
        </Field>
      </Card>

      <div className="sticky bottom-4 flex items-center gap-3 rounded-2xl border border-[#27272A] bg-[#0C0C0E]/95 p-4 backdrop-blur">
        <SaveButton onClick={save} saving={saving} saved={saved} testid="admin-save-area" />
        <GhostButton onClick={() => setDraft(JSON.parse(JSON.stringify(content.service_area || {})))} testid="admin-reset-area">
          Verwerfen
        </GhostButton>
      </div>
    </div>
  );
}
